import { PlusCircleOutlined, UploadOutlined } from "@ant-design/icons";
import ConsumableEditItem from "@Components/consumables/ConsumableEditItem";
import { useCreateConsumable } from "@Components/consumables/mutations";
import { IConsumableEditFormValues } from "@Components/consumables/types";
import PageHelmet from "@Components/shared/PageHelmet";
import { IConsumablePayload } from "@Models/IConsumablePayload";
import ConsumablesService from "@Services/ConsumablesService";
import {Button, Col, Empty, Form, Input, InputNumber, notification, Row, Select, Upload} from "antd";
import Title from "antd/es/typography/Title";
import React, { memo, useEffect, useState } from "react";

import "@Styles/pages/consumablesEditPage.scss";
import LargeSpinner from "@Components/shared/LargeSpinner";

const ConsumablesEditPage: React.FC = () => {
    const [fetching, setFetching] = useState<boolean>(false);
    const [consumable, setConsumable] = useState<IConsumablePayload>({
        products: [],
        consumables: [],
    });

    const [form] = Form.useForm();

    const getAllConsumables = async () => {
        setFetching(true);
        const service = new ConsumablesService();
        await service.getAll().then((res) => {
            setConsumable(res.value);
        });
        setFetching(false);
    };

    const { mutateAsync: createConsumable, isLoading: isCreating } = useCreateConsumable();

    const handleCreateFormSubmit = (values: IConsumableEditFormValues) => {
        createConsumable(values, {
            onSuccess: (res) => {
                if (!res.hasErrors && !!res.value) {
                    notification.success({ message: "New item created." });
                    form.resetFields();
                } else {
                    notification.error({ message: "Error creating new item." });
                }
                getAllConsumables().then();
            },
        }).then();
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const normFile = (e: any) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e && e.fileList;
    };

    const onBeforeUpload = (file) => {
        const types = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
        const valid = types.includes(file.type);
        if (!valid) {
            notification.error({ message: "Image file type is invalid" });
        }
        return valid || Upload.LIST_IGNORE;
    };

    useEffect(() => {
        getAllConsumables().then();
    }, []);

    const productIdsOptions = consumable?.products.map((item) => ({ value: item.productId, label: item.label }));
    return (
        <article className="consumables-edit-page">
            <PageHelmet name={"Edit Consumables"} />
            <Row gutter={[8, 8]} justify="space-between" className="consumables-page-panel">
                <Col span={16} lg={16} md={24} sm={24} xs={24}>
                    <section className="consumables-list">
                        {fetching ? (
                            <LargeSpinner />
                        ) : (
                            consumable?.consumables.length ?
                                consumable?.consumables.map((item) => (
                                    <Row gutter={[32, 32]} justify="space-between">
                                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24} key={item.productCode}>
                                        <ConsumableEditItem
                                            {...item}
                                            products={consumable?.products}
                                            getAllConsumables={getAllConsumables}
                                        />
                                    </Col>
                                    </Row>
                                ))
                                :
                                <Empty
                                    style={{ textAlign: "center" }}
                                    description={"No consumables to display."}
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                            )
                        }
                    </section>
                </Col>
                <Col span={8} lg={8} md={24} sm={24} xs={24}>
                    <Form
                        name="consumable-create"
                        className="consumables-creation-form"
                        form={form}
                        onFinish={handleCreateFormSubmit}
                    >
                        <Title level={5} className="consumables-creation-title">
                            Add new consumable
                        </Title>

                        <Row gutter={[16, 16]} justify="start">
                            <Col className={"consumables-col left"}>
                                <Form.Item
                                    name="name"
                                    label="Name"
                                    rules={[{ required: true, message: "Please give the product a name" }]}
                                >
                                    <Input />
                                </Form.Item>
                                <Form.Item
                                    name="productCode"
                                    label="Code"
                                    rules={[
                                        { whitespace: true, message: "Do not use spaces in the product code" },
                                        { required: true, message: "Please input a product code" },
                                    ]}
                                    normalize={(value) => value.replace(/\s/g, "")}
                                >
                                    <Input />
                                </Form.Item>
                                <Form.Item name="description" label="Description" rules={[{ required: false }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col className={"consumables-col"}>
                                <Form.Item
                                    name="creditPrice"
                                    label="Credits"
                                    rules={[{ required: true, message: "Please enter a price" }]}
                                >
                                    <InputNumber min={0} />
                                </Form.Item>
                                <Form.Item
                                    name="productIds"
                                    label="Products"
                                    rules={[{ required: true, message: "Please select a product" }]}
                                >
                                    <Select mode="multiple" allowClear options={productIdsOptions} />
                                </Form.Item>
                                <Form.Item
                                    label="Image"
                                    name="imageUrl"
                                    valuePropName="fileList"
                                    getValueFromEvent={normFile}
                                    rules={[{ required: true, message: "Please upload an image" }]}
                                >
                                    <Upload
                                        maxCount={1}
                                        className="upload-file"
                                        multiple={false}
                                        accept={"image/png, image/jpeg, image/jpg, image/webp"}
                                        beforeUpload={(file) => onBeforeUpload(file)}
                                    >
                                        <Button className="secondary" icon={<UploadOutlined />}>
                                            Image
                                        </Button>
                                    </Upload>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item>
                            <Button
                                className="secondary"
                                disabled={isCreating}
                                htmlType="submit"
                                icon={<PlusCircleOutlined />}
                                loading={isCreating}
                            >
                                Add
                            </Button>
                        </Form.Item>
                    </Form>
                </Col>
            </Row>
        </article>
    );
};

export default memo(ConsumablesEditPage);
